import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { UnreadProvider } from "@/context/UnreadContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AgencyLayout from "@/layouts/AgencyLayout";
import Dashboard from "@/pages/Dashboard";
import DashboardV2 from "@/pages/Dashboard.v2";
import DashboardV3 from "@/pages/Dashboard.v3";
import SettingsV3 from "@/pages/Settings.v3";
import PlanningV3 from "@/pages/Planning.v3";
import DocumentsV3 from "@/pages/Documents.v3";
import DossierV3 from "@/pages/Dossier.v3";
import EspaceClientV3 from "@/pages/EspaceClient.v3";
import MandatsListV3 from "@/pages/MandatsList.v3";
import MandatCockpitV3 from "@/pages/MandatCockpit.v3";
import VendeurLogin from "@/pages/vendeur/VendeurLogin";
import VendeurMandat from "@/pages/vendeur/VendeurMandat";
import MotionLab from "@/pages/MotionLab";
import CommandCenter from "@/pages/CommandCenter";
import DossierDetail from "@/pages/DossierDetail";
import SavDetail from "@/pages/SavDetail";
import ClientSavView from "@/pages/ClientSavView";
import DisplayScreen from "@/pages/DisplayScreen";
import DisplaySettings from "@/pages/DisplaySettings";
import CommandCenterScreenPreview from "@/pages/CommandCenterScreenPreview";
import ClientView from "@/pages/ClientView";
import ClientInvite from "@/pages/ClientInvite";
import ClientWelcome from "@/pages/ClientWelcome";
import ClientLogin from "@/pages/ClientLogin";
// Note: /app/messages et /app/documents seront réintroduits lorsque les modules
// seront pleinement fonctionnels. Le composant ComingSoon reste disponible dans /pages
// pour un usage futur — non importé ici pour ne pas polluer la production.
import Settings from "@/pages/Settings";
import WorkflowList from "@/pages/WorkflowList";
import WorkflowEditor from "@/pages/WorkflowEditor";
import Planning from "@/pages/Planning";
import DossierList from "@/pages/DossierList";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SplashScreen from "@/pages/SplashScreen";
import TeamPage from "@/pages/TeamPage";
import CommunicationsPage from "@/pages/CommunicationsPage";
import PlatformSinchPage from "@/pages/PlatformSinchPage";
import ActivateInvitation from "@/pages/ActivateInvitation";

// Mockups DA (routes publiques noindex — validation typographie)
import MockupsIndex from "@/pages/mockups/MockupsIndex";
import DashboardMockup from "@/pages/mockups/DashboardMockup";
import DossierMockup from "@/pages/mockups/DossierMockup";
import ClientMockup from "@/pages/mockups/ClientMockup";
import PlanningMockup from "@/pages/mockups/PlanningMockup";
import VehiclesMockup from "@/pages/mockups/VehiclesMockup";

// Marketing (public, indexable)
import MarketingLayout from "@/marketing/MarketingLayout";
import Home from "@/marketing/pages/Home";
import Fonctionnalites from "@/marketing/pages/Fonctionnalites";
import Tarifs from "@/marketing/pages/Tarifs";
import APropos from "@/marketing/pages/APropos";
import Contact from "@/marketing/pages/Contact";
import MentionsLegales from "@/marketing/pages/MentionsLegales";
import PrivateNoIndex from "@/marketing/PrivateNoIndex";

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <ThemeProvider>
        <AuthProvider>
          <UnreadProvider>
          <Toaster />
          <BrowserRouter>
            <Routes>
              {/* Marketing public — indexable */}
              <Route element={<MarketingLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/fonctionnalites" element={<Fonctionnalites />} />
                <Route path="/tarifs" element={<Tarifs />} />
                <Route path="/a-propos" element={<APropos />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
              </Route>

              {/* Auth entry points (noindex) */}
              <Route path="/splash" element={<><PrivateNoIndex /><SplashScreen /></>} />
              <Route path="/login" element={<><PrivateNoIndex /><Login /></>} />
              <Route path="/forgot-password" element={<><PrivateNoIndex /><ForgotPassword /></>} />
              <Route path="/reset-password" element={<><PrivateNoIndex /><ResetPassword /></>} />

              {/* Vue Agence (protégée + noindex) */}
              <Route
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <AgencyLayout />
                    </ProtectedRoute>
                  </>
                }
              >
                {/* /app et /app/dashboard sont maintenant propagés vers Pilotage V4 (hors AgencyLayout). */}
                <Route path="/app/command-center" element={<CommandCenter />} />
                <Route path="/app/dashboard-classic" element={<Dashboard />} />
                <Route path="/app/dashboard-v2" element={<DashboardV2 />} />
                <Route path="/app/dossiers" element={<DossierList />} />
                {/* /app/dossier/:id est maintenant propagé vers Fiche Dossier V3 (hors AgencyLayout). */}
                <Route path="/app/dossier-classic/:id" element={<DossierDetail />} />
                <Route path="/app/sav/:id" element={<SavDetail />} />
                {/* /app/planning est maintenant propagé vers Planning V3 (hors AgencyLayout). */}
                <Route path="/app/planning-classic" element={<Planning />} />
                {/* /app/parametres est maintenant propagé vers Settings V3 (hors AgencyLayout).
                    Les sous-routes /app/parametres/* conservent le layout legacy et pointent
                    sur les modules dédiés (équipe, communications, workflows, écrans). */}
                <Route path="/app/parametres/equipe" element={<TeamPage />} />
                <Route path="/app/parametres/communications" element={<CommunicationsPage />} />
                <Route path="/app/platform/communications" element={<PlatformSinchPage />} />
                <Route path="/app/parametres/workflows" element={<WorkflowList />} />
                <Route path="/app/parametres/workflows/:id" element={<WorkflowEditor />} />
                <Route path="/app/parametres/ecrans" element={<DisplaySettings />} />
                <Route path="/app/command-center/screen" element={<CommandCenterScreenPreview />} />
              </Route>

              {/* Parcours Client (noindex) */}
              <Route path="/collab/activate/:token" element={<><PrivateNoIndex /><ActivateInvitation /></>} />
              <Route path="/invite/:token" element={<><PrivateNoIndex /><ClientInvite /></>} />
              <Route path="/client/login" element={<><PrivateNoIndex /><ClientLogin /></>} />
              {/* Espace Client — Sprint UI 1.0 · propagation étape 6 (finale)
                  Route canonique = /client/welcome  ·  Alias recette = /client/espace-v3
                  Composant : EspaceClient.v3.jsx en mode="client" (session cookie client,
                  aucun endpoint agence utilisé).
                  Legacy : ClientWelcome conservé sur /client/welcome-classic. */}
              <Route path="/client/welcome" element={<><PrivateNoIndex /><EspaceClientV3 mode="client" /></>} />
              <Route path="/client/welcome-classic" element={<><PrivateNoIndex /><ClientWelcome /></>} />
              {/* Alias recette V3 conservé */}
              <Route path="/client/espace-v3" element={<><PrivateNoIndex /><EspaceClientV3 mode="client" /></>} />
              <Route path="/client/espace-v3/:id" element={<><PrivateNoIndex /><EspaceClientV3 mode="client" /></>} />
              {/* Deep-link dossier client — reste sur le legacy ClientWelcome
                  tant que la recette per-dossier V3 n'est pas explicitement demandée. */}
              <Route path="/client/dossier/:dossierId" element={<><PrivateNoIndex /><ClientWelcome /></>} />
              <Route path="/client" element={<><PrivateNoIndex /><ClientView /></>} />
              <Route path="/client/suivi/:id" element={<><PrivateNoIndex /><ClientSavView /></>} />

              {/* Display Screen — Lot 22 · lu par un TV/écran partagé via token
                  Aucune session admin requise (auth par query param). */}
              <Route path="/display/command-center" element={<><PrivateNoIndex /><DisplayScreen /></>} />

              <Route path="/client/:token" element={<><PrivateNoIndex /><ClientInvite /></>} />

              {/* Backward-compat : anciennes URLs de l'app → nouvelle structure */}
              <Route path="/dossiers" element={<Navigate to="/app/dossiers" replace />} />
              <Route path="/dossier/:id" element={<LegacyDossierRedirect />} />
              <Route path="/planning" element={<Navigate to="/app/planning" replace />} />
              <Route path="/parametres/*" element={<Navigate to="/app/parametres" replace />} />

              {/* Mockups Direction Artistique — noindex, publics, validation typo */}
              <Route path="/mockups" element={<><PrivateNoIndex /><MockupsIndex /></>} />
              <Route path="/mockups/dashboard-typo-inter-display" element={<><PrivateNoIndex /><DashboardMockup typography="inter-display" /></>} />
              <Route path="/mockups/dashboard-typo-general-sans" element={<><PrivateNoIndex /><DashboardMockup typography="general-sans" /></>} />
              <Route path="/mockups/dashboard-typo-neue-haas" element={<><PrivateNoIndex /><DashboardMockup typography="neue-haas" /></>} />
              <Route path="/mockups/dossier" element={<><PrivateNoIndex /><DossierMockup /></>} />
              <Route path="/mockups/client" element={<><PrivateNoIndex /><ClientMockup /></>} />
              <Route path="/mockups/planning" element={<><PrivateNoIndex /><PlanningMockup /></>} />
              <Route path="/mockups/vehicules" element={<><PrivateNoIndex /><VehiclesMockup /></>} />

              {/* Motion Lab — laboratoire interne du langage d'animation (noindex) */}
              <Route path="/motion-lab" element={<><PrivateNoIndex /><MotionLab /></>} />

              {/* Dashboard V3 preview — WYVIO UI 1.0 · Sprint UI 1.0 · propagation étape 4
                  Routes canoniques = /app + /app/dashboard  ·  Alias = /app/dashboard-v3
                  Composant : Dashboard.v3.jsx (Pilotage V4 — aucun redesign, aucune logique changée).
                  Legacy conservés : /app/command-center (CommandCenter), /app/dashboard-v2 (DashboardV2),
                  /app/dashboard-classic (Dashboard legacy). */}
              <Route
                path="/app"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DashboardV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              <Route
                path="/app/dashboard"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DashboardV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              {/* Alias temporaire preview conservé (rétrocompat + démos) */}
              <Route
                path="/app/dashboard-v3"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DashboardV3 />
                    </ProtectedRoute>
                  </>
                }
              />

              {/* Settings (production) — Sprint UI 1.0 · propagation étape 2
                  Route canonique = /app/parametres  ·  Alias = /app/parametres-v3
                  Composant : Settings.v3.jsx (aucun redesign, aucune logique changée).
                  Les sous-routes /app/parametres/* (équipe, communications, workflows, écrans)
                  restent inchangées sous AgencyLayout legacy. */}
              <Route
                path="/app/parametres"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <SettingsV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              {/* Settings V3 preview — Réglages WYVIO UI 1.0 (noindex, protégé) — ALIAS conservé */}
              <Route
                path="/app/parametres-v3"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <SettingsV3 />
                    </ProtectedRoute>
                  </>
                }
              />

              {/* Planning (production) — Sprint UI 1.0 · propagation étape 3
                  Route canonique = /app/planning  ·  Alias = /app/planning-v3
                  Composant : Planning.v3.jsx (aucun redesign, aucune logique changée). */}
              <Route
                path="/app/planning"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <PlanningV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              {/* Planning V3 preview — WYVIO UI 1.0 (noindex, protégé) — ALIAS conservé */}
              <Route
                path="/app/planning-v3"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <PlanningV3 />
                    </ProtectedRoute>
                  </>
                }
              />

              {/* Documents (production) — Sprint UI 1.0 · propagation étape 1
                  Route canonique = /app/documents  ·  Alias = /app/documents-v3
                  Composant : Documents.v3.jsx (aucun redesign, aucune logique changée). */}
              <Route
                path="/app/documents"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DocumentsV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              {/* Documents V3 preview — WYVIO UI 1.0 (noindex, protégé) — ALIAS conservé */}
              <Route
                path="/app/documents-v3"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DocumentsV3 />
                    </ProtectedRoute>
                  </>
                }
              />

              {/* Fiche Dossier V3 — Sprint UI 1.0 · propagation étape 5
                  Route canonique = /app/dossier/:id  ·  Alias = /app/dossier-v3/:id
                  Composant : Dossier.v3.jsx (aucun redesign, aucune logique changée).
                  Legacy : DossierDetail conservé sur /app/dossier-classic/:id. */}
              <Route
                path="/app/dossier/:id"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DossierV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              {/* Dossier V3 preview — WYVIO UI 1.0 (noindex, protégé) — ALIAS conservé */}
              <Route
                path="/app/dossier-v3/:id"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DossierV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              <Route
                path="/app/dossier-v3"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <DossierV3 />
                    </ProtectedRoute>
                  </>
                }
              />

              {/* Espace Client V3 preview — agence-side (noindex, protégé) */}
              <Route
                path="/app/espace-client-v3/:id"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <EspaceClientV3 mode="preview" />
                    </ProtectedRoute>
                  </>
                }
              />
              <Route
                path="/app/espace-client-v3"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <EspaceClientV3 mode="preview" />
                    </ProtectedRoute>
                  </>
                }
              />

              {/* Espace vendeur (Lot M1) — mobile-first, sans compte, noindex.
                  Accès magic-link 30j OU référence + code. */}
              <Route path="/vendeur/login" element={<><PrivateNoIndex /><VendeurLogin /></>} />
              <Route path="/vendeur/magic/:token" element={<><PrivateNoIndex /><VendeurLogin /></>} />
              <Route path="/vendeur/mandat/:id" element={<><PrivateNoIndex /><VendeurMandat /></>} />
              <Route path="/vendeur" element={<><PrivateNoIndex /><VendeurMandat /></>} />

              {/* Mandats (Lot M1 · fondations) — routes canoniques agence */}
              <Route
                path="/app/mandats"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <MandatsListV3 />
                    </ProtectedRoute>
                  </>
                }
              />
              <Route
                path="/app/mandat/:id"
                element={
                  <>
                    <PrivateNoIndex />
                    <ProtectedRoute>
                      <MandatCockpitV3 />
                    </ProtectedRoute>
                  </>
                }
              />

              {/* Catch-all — la home publique */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </UnreadProvider>
        </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </div>
  );
}

// Redirige /dossier/:id → /app/dossier/:id
function LegacyDossierRedirect() {
  const path = window.location.pathname.replace(/^\/dossier\//, "/app/dossier/");
  return <Navigate to={path} replace />;
}

export default App;
